import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, XCircle, TrendingUp } from 'lucide-react';
import { Card } from '../ds/Card';
import { Button } from '../ds/Button';
import { MetricDisplay } from './MetricDisplay';
import api from '../../services/api';

/**
 * ShotCurationPanel - Phase 1: Shot Curation for v4
 *
 * Identifies potential mishits and allows users to curate their shot data
 * before analyzing fitting recommendations.
 *
 * Features:
 * - Automatic flagging based on smash factor, offline distance, ball speed, carry outliers
 * - Manual toggle for each shot
 * - Real-time averages update as shots are included/excluded
 * - Shows reasons for flagged shots
 *
 * @param {Object} props
 * @param {Array} props.shots - Array of shot objects with metrics
 * @param {string} props.clubType - Club type for smash factor thresholds
 * @param {Function} props.onCuratedData - Callback with curated averages
 * @param {Function} props.onCancel - Callback to skip curation
 */
// Helper function to format shots for API
const formatShotsForAPI = (shots) => {
  return shots.map((shot, idx) => ({
    id: shot.id || idx + 1,
    ball_speed: shot.ball_speed,
    launch_angle: shot.launch_angle,
    spin_rate: shot.spin_rate,
    carry_distance: shot.carry_distance || shot.carry,
    total_distance: shot.total_distance || shot.total,
    smash_factor: shot.smash_factor,
    offline_distance: shot.offline_distance || shot.offline,
    max_height: shot.max_height || shot.apex,
    descent_angle: shot.descent_angle || shot.descent,
  }));
};


export const ShotCurationPanel = ({ shots, clubType, onCuratedData, onCancel }) => {
  const [curationResults, setCurationResults] = useState([]);
  const [userOverrides, setUserOverrides] = useState({});
  const [curatedAverages, setCuratedAverages] = useState({});
  const [allShotsAverages, setAllShotsAverages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Run curation analysis on mount
  useEffect(() => {
    analyzeShotsForCuration();
  }, [shots, clubType]);

  const analyzeShotsForCuration = async () => {
    setLoading(true);
    setError(null);

    try {
      const formattedShots = formatShotsForAPI(shots);

      const response = await api.post('/analyze/curate-shots', {
        shots: formattedShots,
        club_type: clubType,
        user_overrides: userOverrides,
      });

      setCurationResults(response.data.curation_results || []);
      setCuratedAverages(response.data.curated_averages || {});
      setAllShotsAverages(response.data.all_shots_averages || {});
    } catch (err) {
      console.error('Shot curation failed:', err);
      setError(err.response?.data?.detail || 'Failed to analyze shots');
    } finally {
      setLoading(false);
    }
  };

  const toggleShot = async (shotId) => {
    // Find current state
    const currentResult = curationResults.find(r => r.shot_id === shotId);
    if (!currentResult) return;

    // Toggle the state
    const newIncludeState = !currentResult.include;

    // Update user overrides
    const newOverrides = { ...userOverrides, [shotId]: newIncludeState };
    setUserOverrides(newOverrides);

    // Re-run curation with new overrides
    try {
      const formattedShots = formatShotsForAPI(shots);

      const response = await api.post('/analyze/curate-shots', {
        shots: formattedShots,
        club_type: clubType,
        user_overrides: newOverrides,
      });

      setCurationResults(response.data.curation_results || []);
      setCuratedAverages(response.data.curated_averages || {});
    } catch (err) {
      console.error('Failed to update curation:', err);
    }
  };

  const handleProceed = () => {
    // Return the curated averages to parent
    onCuratedData(curatedAverages);
  };

  const handleUseAll = () => {
    // Return all shots averages to parent
    onCuratedData(allShotsAverages);
  };

  const includedCount = curationResults.filter(r => r.include).length;
  const excludedCount = curationResults.length - includedCount;

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted">Analyzing shots for potential mishits...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center space-x-3 text-destructive mb-4">
          <AlertCircle className="w-5 h-5" />
          <p className="font-semibold">Shot Curation Error</p>
        </div>
        <p className="text-muted mb-4">{error}</p>
        <Button onClick={onCancel} variant="secondary">
          Go Back
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Shot Curation</h2>
            <p className="text-muted">
              Review your shots and exclude any mishits before analysis
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted">
              <span className="text-success">{includedCount} included</span>
              {' · '}
              <span className="text-destructive">{excludedCount} excluded</span>
            </div>
          </div>
        </div>

        {/* Averages Comparison */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <h3 className="font-semibold text-white">Curated Averages</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <MetricDisplay label="Ball Speed" value={curatedAverages.ball_speed} unit="mph" />
              <MetricDisplay label="Launch" value={curatedAverages.launch_angle} unit="°" />
              <MetricDisplay label="Spin" value={curatedAverages.spin_rate} unit="rpm" />
              <MetricDisplay label="Carry" value={curatedAverages.carry_distance} unit="yds" />
              <MetricDisplay label="Smash" value={curatedAverages.smash_factor} />
              <MetricDisplay label="Max Height" value={curatedAverages.max_height} unit="ft" />
            </div>
          </div>

          <div className="bg-border/10 border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <TrendingUp className="w-5 h-5 text-muted" />
              <h3 className="font-semibold text-muted">All Shots</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <MetricDisplay label="Ball Speed" value={allShotsAverages.ball_speed} unit="mph" muted />
              <MetricDisplay label="Launch" value={allShotsAverages.launch_angle} unit="°" muted />
              <MetricDisplay label="Spin" value={allShotsAverages.spin_rate} unit="rpm" muted />
              <MetricDisplay label="Carry" value={allShotsAverages.carry_distance} unit="yds" muted />
              <MetricDisplay label="Smash" value={allShotsAverages.smash_factor} muted />
              <MetricDisplay label="Max Height" value={allShotsAverages.max_height} unit="ft" muted />
            </div>
          </div>
        </div>
      </Card>

      {/* Shot List */}
      <Card className="p-6">
        <h3 className="font-semibold text-white mb-4">Individual Shots</h3>
        <div className="space-y-2">
          {curationResults.map((result) => {
            const shot = shots.find(s => (s.id || shots.indexOf(s) + 1) === result.shot_id);
            if (!shot) return null;
            const isIncluded = result.include;
            const hasFlagReasons = result.reasons && result.reasons.length > 0;

            return (
              <div
                key={result.shot_id}
                className={`flex items-center space-x-4 p-4 rounded-lg border transition-all ${
                  isIncluded
                    ? 'bg-surface border-border'
                    : 'bg-destructive/5 border-destructive/20 opacity-60'
                }`}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isIncluded}
                  onChange={() => toggleShot(result.shot_id)}
                  className="w-5 h-5 rounded border-border bg-subtle text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                />

                {/* Shot Number */}
                <div className="w-16">
                  <span className="font-mono text-sm text-muted">
                    Shot {result.shot_id}
                  </span>
                </div>

                {/* Metrics */}
                <div className="flex-1 grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted">Ball Speed: </span>
                    <span className="text-white font-semibold">
                      {shot.ball_speed?.toFixed(1) || '--'} mph
                    </span>
                  </div>
                  <div>
                    <span className="text-muted">Carry: </span>
                    <span className="text-white font-semibold">
                      {(shot.carry_distance || shot.carry)?.toFixed(0) || '--'} yds
                    </span>
                  </div>
                  <div>
                    <span className="text-muted">Smash: </span>
                    <span className="text-white font-semibold">
                      {shot.smash_factor?.toFixed(2) || '--'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted">Offline: </span>
                    <span className="text-white font-semibold">
                      {(shot.offline_distance || shot.offline)?.toFixed(0) || '--'} yds
                    </span>
                  </div>
                </div>

                {/* Flag Icon & Reasons */}
                {hasFlagReasons && (
                  <div className="flex items-center space-x-2">
                    <XCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                    <div className="text-xs text-destructive max-w-xs">
                      {result.reasons.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <Button onClick={onCancel} variant="tertiary">
          Cancel
        </Button>
        <div className="flex space-x-3">
          <Button onClick={handleUseAll} variant="secondary">
            Use All Shots
          </Button>
          <Button onClick={handleProceed} variant="primary">
            Proceed with {includedCount} Shots
          </Button>
        </div>
      </div>
    </div>
  );
}
